import { EditIcon, IconButton, Input, TrashIcon } from '@/lib';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { TemplatesFilterChip, TemplatesViewMode } from '../../config';

type WhatsappTemplateListControlsProps = {
  templateFilters: TemplatesFilterChip[];
  selectedFilter: TemplatesFilterChip;
  onFilterChange: (filter: TemplatesFilterChip) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  viewMode: TemplatesViewMode;
  onViewModeChange: (mode: TemplatesViewMode) => void;
  filteredCount: number;
  totalCount: number;
  onEditHub: () => void;
  onDeleteHub: () => void;
};

export const WhatsappTemplateListControls: FC<WhatsappTemplateListControlsProps> = ({
  templateFilters,
  selectedFilter,
  onFilterChange,
  searchValue,
  onSearchChange,
  viewMode,
  onViewModeChange,
  filteredCount,
  totalCount,
  onEditHub,
  onDeleteHub,
}) => {
  const { t } = useTranslation('campaign');

  return (
    <div className='space-y-3 rounded-2xl border border-secondary-border bg-white p-4'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{t('whatsappTemplates.templateControls')}</p>
        <p className='text-sm text-secondary-600'>
          {filteredCount} of {totalCount}
        </p>
      </div>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-row items-center justify-between'>
          <div className='flex flex-wrap gap-2'>
            {templateFilters.map((filter) => (
              <button
                key={filter}
                type='button'
                onClick={() => onFilterChange(filter)}
                className={twMerge(
                  'h-8 rounded-full border px-3 text-xs font-semibold uppercase tracking-[0.12em] border-secondary-border bg-white text-secondary-600 hover:bg-gray-50',
                  selectedFilter === filter && 'border-primary-500 bg-primary-50 text-primary-700'
                )}
              >
                {filter}
              </button>
            ))}
          </div>
          <div className='flex items-center gap-2'>
            <IconButton
              type='button'
              intent='secondary'
              className='h-10 w-full shrink-0 justify-center sm:w-auto'
              leadingIcon={<EditIcon className='size-4' />}
              text={t('whatsappTemplates.editTemplatesHub')}
              onClick={onEditHub}
            />
            <IconButton
              type='button'
              intent='tertiary'
              className='h-10 w-full shrink-0 justify-center border border-red-600 text-red-600 hover:text-red-800 sm:w-auto'
              leadingIcon={<TrashIcon className='size-4' />}
              text={t('whatsappTemplates.deleteTemplatesHub')}
              onClick={onDeleteHub}
            />
          </div>
        </div>

        <div className='flex w-full items-center justify-between'>
          <Input
            type='text'
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t('whatsappTemplates.searchPlaceholder')}
            className='h-10 border-secondary-border bg-white text-sm text-secondary-500 sm:w-[280px]'
          />
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => onViewModeChange('cards')}
              className={twMerge(
                'h-10 rounded-lg border border-secondary-border bg-white px-3 text-xs font-semibold uppercase tracking-[0.12em] text-secondary-600 hover:bg-gray-50',
                viewMode === 'cards' && 'border-primary-500 bg-primary-50 text-primary-700'
              )}
            >
              {t('whatsappTemplates.cardsView')}
            </button>
            <button
              type='button'
              onClick={() => onViewModeChange('table')}
              className={twMerge(
                'h-10 rounded-lg border border-secondary-border bg-white px-3 text-xs font-semibold uppercase tracking-[0.12em] text-secondary-600 hover:bg-gray-50',
                viewMode === 'table' && 'border-primary-500 bg-primary-50 text-primary-700'
              )}
            >
              {t('whatsappTemplates.tableView')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
